import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Autocomplete, Box, Button, Chip, FormControlLabel, Grid, IconButton, InputAdornment, Link, Menu, MenuItem,
  Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AddCircleRounded from '@mui/icons-material/AddCircleRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import {useSnackbar} from 'notistack';
import withLayout from '../../hocs/Layout';
import PageTitle from '../../common/PageTitle';
import Loader from '../../common/Loader';
import MUIModal from '../../common/MUIModal';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import {FormProvider, RHFSearchableSelectField, RHFSelectField, RHFTextField} from '../../common/hook-form';
import {
  alertStripSx, badgeSx, btnRowSx, filterBarSx, helpTextSx, helpTextTypeSx, pageContainerSx, primaryBtnSx,
  secondaryBtnSx,
} from '../../styles/patterns';
import {cellStackSx, primaryTextSx, tertiaryTextSx} from '../orders/list2/cellStyles';
import {ERROR, INFO, MUTED, NAVY, PRIMARY_BLUE, SUBTLE, SUCCESS, WARNING} from '../../styles/foundation/colors';
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography';
import {GAP} from '../../styles/foundation/spacing';
import {
  addMapping, deleteMapping, downloadMappings, downloadTemplate, errorText, fetchConfig, fetchPickupPincodes,
  fetchStates, resolvePickup, saveBlob, saveSettings, uploadMappings,
} from './shopifyEddLocationApi';

const PIN = /^\d{6}$/;
const lc = (s) => String(s ?? '').trim().toLowerCase();
const digits = (s) => (s || '').replace(/\D/g, '').slice(0, 6);
const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : '');

const MATCH_TEXT = {
  PINCODE: 'pincode rule',
  STATE: 'state rule',
  DEFAULT: 'default pickup, no rule matched',
  NONE: 'no rule matched and no default pickup',
  DISABLED: 'switched off',
  NOT_CONFIGURED: 'nothing configured yet',
};

const labelSx = {fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.medium, color: NAVY};
const captionSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE};
const fieldSx = {
  '& .MuiInputLabel-root': {fontFamily: FONT, fontSize: SIZE['4xs'] + ' !important'},
  '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE['2xs']},
};
const menuItemSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75};

/** What Shopify gets right now, as a badge. */
const statusOf = (config, rules) => {
  if (!config) return null;
  if (config.saved === false) return {label: 'Not configured', color: MUTED};
  if (config.enabled === false) return {label: 'Disabled', color: WARNING};
  const def = config.defaultPickupPincode;
  return {
    label: `Live · ${def ? `default ${def}` : 'no default'} · ${rules} rule${rules === 1 ? '' : 's'}`,
    color: def || rules ? SUCCESS : WARNING,
  };
};

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const downloadErrors = (rows) => {
  const lines = ['Row,Drop,Pickup Pincode,Reason', ...rows.map((r) => [r.row, r.drop, r.pickupPincode, r.reason].map(csvCell).join(','))];
  saveBlob(new Blob([lines.join('\n')], {type: 'text/csv;charset=utf-8'}), 'pickup_drop_mapping_upload_errors.csv');
};

/**
 * Settings → Shopify EDD Pickup Mapping (merchant panel). Which warehouse (pickup pincode) ships to which state or
 * customer pincode, so the delivery date on the merchant's Shopify store is quoted on the right lane. The default
 * pickup and the Enabled switch save themselves; rules come from a CSV merge, a row dialog, or in-place edits.
 */
function ShopifyEddLocation() {
  const {enqueueSnackbar} = useSnackbar();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [states, setStates] = useState([]);
  const [pickupHints, setPickupHints] = useState([]);

  const [enabled, setEnabled] = useState(true);
  const [defaultPickup, setDefaultPickup] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const [filter, setFilter] = useState('');
  const [showMissing, setShowMissing] = useState(false);
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [editing, setEditing] = useState(null); // {drop, value}
  const [busyRow, setBusyRow] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addInitialDrop, setAddInitialDrop] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloading, setDownloading] = useState('');

  const [testPin, setTestPin] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');

  const applyConfig = useCallback((c) => {
    setConfig(c);
    setEnabled(c?.enabled !== false);
    setDefaultPickup(c?.defaultPickupPincode || '');
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    fetchConfig()
      .then(applyConfig)
      .catch((e) => setLoadError(errorText(e, 'Could not load your pickup drop mapping')))
      .finally(() => setLoading(false));
    fetchStates().then(setStates).catch(() => setStates([]));
    fetchPickupPincodes().then(setPickupHints).catch(() => setPickupHints([]));
  }, [applyConfig]);

  useEffect(() => { load(); }, [load]);

  // ── derived ───────────────────────────────────────────────────────────────
  const mappings = useMemo(() => config?.mappings || [], [config]);
  const stateRows = useMemo(() => mappings.filter((m) => m.dropType === 'STATE'), [mappings]);
  const pinRows = mappings.length - stateRows.length;
  const status = statusOf(config, mappings.length);
  const missingStates = useMemo(() => {
    const mapped = new Set(stateRows.map((m) => lc(m.dropValue)));
    return states.filter((s) => !mapped.has(lc(s)));
  }, [states, stateRows]);
  const visible = useMemo(() => {
    const q = lc(filter);
    if (!q) return mappings;
    return mappings.filter((m) => lc(m.dropValue).includes(q) || String(m.pickupPincode).includes(q));
  }, [mappings, filter]);
  const highlightDrop = testResult?.matchedDrop ? lc(testResult.matchedDrop) : null;

  useEffect(() => { setPage(0); }, [filter, sizePerPage]);

  // Jump to the page holding the rule the tester matched.
  useEffect(() => {
    if (!highlightDrop) return;
    const idx = visible.findIndex((m) => lc(m.dropValue) === highlightDrop);
    if (idx >= 0) setPage(Math.floor(idx / sizePerPage));
  }, [highlightDrop, visible, sizePerPage]);

  // ── settings: each control saves itself ───────────────────────────────────
  const defaultPickupBad = defaultPickup !== '' && !PIN.test(defaultPickup);
  const defaultPickupDirty = !!config && !defaultPickupBad && (defaultPickup || '') !== (config.defaultPickupPincode || '');

  const saveSetting = async (patch) => {
    setSavingSettings(true);
    try {
      applyConfig(await saveSettings(patch));
    } catch (e) {
      applyConfig(config); // roll the controls back to the saved values
      enqueueSnackbar(errorText(e, 'Could not save'), {variant: 'error'});
    } finally {
      setSavingSettings(false);
    }
  };
  const onToggleEnabled = (e) => {
    setEnabled(e.target.checked);
    saveSetting({enabled: e.target.checked});
  };
  const commitDefaultPickup = () => {
    if (defaultPickupDirty) saveSetting({defaultPickupPincode: defaultPickup || null});
  };

  // ── rules ─────────────────────────────────────────────────────────────────
  const onDelete = async (row) => {
    setBusyRow(row.dropValue);
    try {
      applyConfig(await deleteMapping(row.dropValue));
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not remove the rule'), {variant: 'error'});
    } finally {
      setBusyRow('');
    }
  };

  const commitEdit = async () => {
    if (!editing) return;
    const {drop, value} = editing;
    const row = mappings.find((m) => m.dropValue === drop);
    setEditing(null);
    if (!row || !PIN.test(value) || value === row.pickupPincode) return;
    setBusyRow(drop);
    try {
      applyConfig(await addMapping({drop, pickupPincode: value}));
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not change the pickup'), {variant: 'error'});
    } finally {
      setBusyRow('');
    }
  };

  const openAdd = (initialDrop = '') => { setAddInitialDrop(initialDrop); setAddOpen(true); };

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const res = await (kind === 'template' ? downloadTemplate() : downloadMappings());
      saveBlob(res.data, kind === 'template' ? 'pickup_drop_mapping_template.csv' : 'pickup_drop_mapping.csv');
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed'), {variant: 'error'});
    } finally {
      setDownloading('');
    }
  };

  // ── tester: runs itself once six digits are typed ─────────────────────────
  useEffect(() => {
    if (!PIN.test(testPin)) { setTestResult(null); setTestError(''); return undefined; }
    const t = setTimeout(() => {
      setTesting(true);
      setTestError('');
      resolvePickup(testPin)
        .then(setTestResult)
        .catch((e) => { setTestResult(null); setTestError(errorText(e, 'Lookup failed')); })
        .finally(() => setTesting(false));
    }, 400);
    return () => clearTimeout(t);
  }, [testPin, config]);

  // ── grid ──────────────────────────────────────────────────────────────────
  const columns = [
    {
      field: 'dropType', headerName: 'Type', minWidth: 110, width: 110, sortable: false,
      renderCell: ({row}) => (
        <Box sx={cellStackSx}>
          <Box sx={badgeSx(row.dropType === 'STATE' ? PRIMARY_BLUE : SUBTLE)}>{row.dropType === 'STATE' ? 'State' : 'Pincode'}</Box>
        </Box>
      ),
    },
    {
      field: 'dropValue', headerName: 'Drop (state or customer pincode)', flex: 1, minWidth: 220, sortable: false,
      renderCell: ({row}) => (
        <Box sx={{...cellStackSx, flexDirection: 'row', alignItems: 'center', gap: 1}}>
          <Typography sx={primaryTextSx}>{row.dropValue}</Typography>
          {highlightDrop && lc(row.dropValue) === highlightDrop && <Box sx={badgeSx(SUCCESS)}>matched</Box>}
        </Box>
      ),
    },
    {
      field: 'pickupPincode', headerName: 'Pickup pincode', minWidth: 180, width: 200, sortable: false,
      renderCell: ({row}) => {
        const isEditing = editing?.drop === row.dropValue;
        if (isEditing) {
          return (
            <TextField size='small' autoFocus value={editing.value} sx={{...fieldSx, width: 120, my: 0.5}}
              onChange={(e) => setEditing({drop: row.dropValue, value: digits(e.target.value)})}
              onBlur={commitEdit}
              onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null); }}
              error={editing.value !== '' && !PIN.test(editing.value)}
              inputProps={{'aria-label': `Pickup pincode for ${row.dropValue}`}} />
          );
        }
        return (
          <Tooltip title='Click to change' placement='right'>
            <Box sx={cellStackSx} onClick={() => busyRow !== row.dropValue && setEditing({drop: row.dropValue, value: row.pickupPincode})}>
              <Typography sx={{...primaryTextSx, cursor: 'text', borderBottom: `1px dashed ${SUBTLE}`, display: 'inline-block'}}>{row.pickupPincode}</Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions', headerName: '', minWidth: 60, width: 60, sortable: false, align: 'right',
      renderCell: ({row}) => (
        <Tooltip title='Remove rule'>
          <span>
            <IconButton size='small' disabled={busyRow === row.dropValue} onClick={() => onDelete(row)} aria-label={`Remove rule for ${row.dropValue}`}>
              <DeleteOutlineRounded fontSize='small' sx={{color: ERROR}} />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <PageTitle subTitle='Which warehouse ships to which state or pincode, used to quote delivery dates on your store'>
        Setup Pickup Drop Mapping
      </PageTitle>
      {loading && <Loader />}

      {loadError && (
        <Box sx={{...alertStripSx(ERROR), mb: 2}}>
          <InfoOutlined fontSize='small' sx={{color: ERROR}} />
          <span>{loadError}</span>
          <Button onClick={load} sx={{...secondaryBtnSx, ml: 'auto'}}>Retry</Button>
        </Box>
      )}

      {!loading && !loadError && config && (
        <>
          <Box sx={helpTextSx}>
            <InfoOutlined fontSize='small' sx={{color: INFO, mt: '1px'}} />
            <Typography sx={helpTextTypeSx}>
              Your store (for example Shopify) asks which warehouse ships to a customer pincode so it can quote a delivery date on the
              right lane. A pincode is resolved in this order: its own pincode rule, then its state rule, then the default pickup
              pincode. With one warehouse, set only the default. This never changes how your orders are allocated.
            </Typography>
          </Box>

          {/* ── Settings + tester ──────────────────────────────────────────── */}
          <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: GAP.lg}}>
            <Autocomplete
              freeSolo
              options={pickupHints}
              inputValue={defaultPickup}
              onInputChange={(_, v) => setDefaultPickup(digits(v))}
              onBlur={commitDefaultPickup}
              onChange={(_, v, reason) => { if (reason === 'selectOption' && v) saveSetting({defaultPickupPincode: v}); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitDefaultPickup(); } }}
              disabled={savingSettings}
              sx={{width: 230}}
              renderInput={(params) => (
                <TextField {...params} size='small' label='Default pickup pincode' error={defaultPickupBad} sx={fieldSx}
                  helperText={defaultPickupBad ? 'Must be 6 digits' : defaultPickupDirty ? 'Press Enter to save' : undefined}
                  FormHelperTextProps={{sx: {position: 'absolute', bottom: -18, m: 0, fontFamily: FONT, fontSize: SIZE['3xs']}}} />
              )} />
            <FormControlLabel sx={{ml: 0}}
              control={<Switch size='small' checked={enabled} disabled={savingSettings} onChange={onToggleEnabled} />}
              label={<Typography sx={labelSx}>Enabled</Typography>} />
            {status && <Box sx={badgeSx(status.color)}>{status.label}</Box>}
            {config.lastUploadAt && <Typography sx={captionSx}>uploaded {fmt(config.lastUploadAt)}</Typography>}

            <Box sx={{ml: {md: 'auto'}, display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap'}}>
              {testError && <Typography sx={{...captionSx, color: ERROR}}>{testError}</Typography>}
              {testResult && !testing && (
                <Typography sx={{...captionSx, color: testResult.found ? SUCCESS : WARNING}}>
                  {testResult.dropState ? `${testResult.dropState} · ` : ''}
                  {testResult.found ? <>pickup <b>{testResult.pickupPincode}</b></> : <b>no pickup</b>}
                  {' · '}{MATCH_TEXT[testResult.matchedBy] || testResult.matchedBy}
                </Typography>
              )}
              <TextField size='small' placeholder='Test a customer pincode' value={testPin} onChange={(e) => setTestPin(digits(e.target.value))}
                sx={{...fieldSx, width: 220, flexShrink: 0}}
                InputProps={{startAdornment: <InputAdornment position='start'><SearchRounded fontSize='small' sx={{color: testing ? PRIMARY_BLUE : MUTED}} /></InputAdornment>}} />
            </Box>
          </Box>

          {/* ── Toolbar ────────────────────────────────────────────────────── */}
          <Box sx={{...btnRowSx, justifyContent: 'flex-end', mb: 2}}>
            <Button startIcon={<UploadFileRounded />} onClick={() => setUploadOpen(true)} sx={primaryBtnSx}>Upload CSV</Button>
            <Button startIcon={<AddCircleRounded />} onClick={() => openAdd()} sx={secondaryBtnSx}>Add rule</Button>
            <Tooltip title='More'>
              <IconButton size='small' aria-label='More actions' onClick={(e) => setMenuAnchor(e.currentTarget)}><MoreVertRounded fontSize='small' /></IconButton>
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem sx={menuItemSx} disabled={downloading === 'template'} onClick={() => { setMenuAnchor(null); download('template'); }}>Download template</MenuItem>
              <MenuItem sx={menuItemSx} disabled={!mappings.length || downloading === 'current'} onClick={() => { setMenuAnchor(null); download('current'); }}>Download current rules</MenuItem>
            </Menu>
          </Box>

          {/* ── Rules ──────────────────────────────────────────────────────── */}
          {mappings.length === 0 ? (
            <Box sx={alertStripSx(INFO)}>
              <InfoOutlined fontSize='small' sx={{color: INFO}} />
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, color: NAVY}}>
                No rules yet, so every customer pincode uses the default pickup pincode. Ship from more than one warehouse?{' '}
                <Link component='button' onClick={() => download('template')} sx={{fontFamily: FONT, fontSize: SIZE.sm, verticalAlign: 'baseline'}}>Download the template</Link>,
                fill it in and upload it.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{...filterBarSx, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: GAP.md}}>
                <TextField size='small' placeholder='Filter by state or pincode' value={filter} onChange={(e) => setFilter(e.target.value)}
                  sx={{...fieldSx, width: 260}}
                  InputProps={{startAdornment: <InputAdornment position='start'><SearchRounded fontSize='small' sx={{color: MUTED}} /></InputAdornment>}} />
                <Typography sx={captionSx}>{stateRows.length} state · {pinRows} pincode</Typography>
                {states.length > 0 && (
                  <Chip size='small' variant={showMissing ? 'filled' : 'outlined'}
                    label={missingStates.length ? `${missingStates.length} of ${states.length} states have no rule` : 'Every state has a rule'}
                    onClick={missingStates.length ? () => setShowMissing((v) => !v) : undefined}
                    sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: missingStates.length ? WARNING : SUCCESS, borderColor: missingStates.length ? WARNING : SUCCESS,
                      bgcolor: showMissing ? `${WARNING}18` : 'transparent'}} />
                )}
                {showMissing && missingStates.length > 0 && (
                  <Box sx={{width: '100%', display: 'flex', flexWrap: 'wrap', gap: GAP.xs, pt: 0.5}}>
                    <Typography sx={{...captionSx, width: '100%'}}>These states fall back to the default pickup. Click one to add its rule.</Typography>
                    {missingStates.map((s) => (
                      <Chip key={s} size='small' variant='outlined' label={s} onClick={() => openAdd(s)} sx={{fontFamily: FONT, fontSize: SIZE['2xs']}} />
                    ))}
                  </Box>
                )}
              </Box>
              <ProzoDataGrid
                rows={visible}
                getRowId={(row) => row.dropValue}
                columns={columns}
                paginationMode='client'
                pagination
                page={page}
                setPage={setPage}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                rowCount={visible.length}
                rowsPerPageOptions={[50, 100, 500]}
                disableColumnMenu
              />
            </>
          )}
        </>
      )}

      <AddRuleModal open={addOpen} initialDrop={addInitialDrop} onClose={() => setAddOpen(false)} states={states} pickupHints={pickupHints}
        onSaved={(c) => { applyConfig(c); setAddOpen(false); }} />
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} existing={mappings.length}
        onDone={() => { setUploadOpen(false); load(); }} onTemplate={() => download('template')} />
    </>
  );
}

const RULE_TYPES = [
  {value: 'STATE', label: 'A whole state'},
  {value: 'PINCODE', label: 'One customer pincode'},
];

const ruleSchema = Yup.object().shape({
  ruleType: Yup.string().required('Choose what the rule applies to'),
  state: Yup.string().when('ruleType', {is: 'STATE', then: (s) => s.required('Pick a state')}),
  dropPincode: Yup.string().when('ruleType', {is: 'PINCODE', then: (s) => s.matches(PIN, 'Customer pincode must be 6 digits')}),
  pickupPincode: Yup.string().matches(PIN, 'Pickup pincode must be 6 digits'),
});

/** Add one rule. Opened from the toolbar, or from a state chip with that state pre-selected. */
function AddRuleModal({open, initialDrop, onClose, states, pickupHints, onSaved}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const methods = useForm({
    resolver: yupResolver(ruleSchema),
    defaultValues: {ruleType: 'STATE', state: '', dropPincode: '', pickupPincode: ''},
  });
  const {handleSubmit, reset, watch, setValue} = methods;
  const ruleType = watch('ruleType');
  const stateOptions = useMemo(() => states.map((s) => ({value: s, label: s})), [states]);

  useEffect(() => {
    if (!open) return;
    const preset = initialDrop && states.some((s) => lc(s) === lc(initialDrop)) ? initialDrop : '';
    reset({ruleType: 'STATE', state: preset, dropPincode: '', pickupPincode: ''});
    setError('');
  }, [open, initialDrop, states, reset]);

  const onSubmit = async (values) => {
    setBusy(true);
    setError('');
    try {
      const drop = values.ruleType === 'STATE' ? values.state : values.dropPincode;
      onSaved(await addMapping({drop, pickupPincode: values.pickupPincode}));
    } catch (e) {
      setError(errorText(e, 'Could not save the rule'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MUIModal open={open} handleClose={() => !busy && onClose()} title='Add a rule' maxWidth='xs'>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{pt: 1}}>
          <Grid item xs={12}>
            <RHFSelectField name='ruleType' label='Applies to' options={RULE_TYPES} width='100%' />
          </Grid>
          <Grid item xs={12}>
            {ruleType === 'STATE'
              ? <RHFSearchableSelectField name='state' label='State' options={stateOptions} width='100%' noOptionsText='No such state' />
              : <RHFTextField name='dropPincode' label='Customer pincode' inputProps={{maxLength: 6, inputMode: 'numeric'}} />}
          </Grid>
          <Grid item xs={12}>
            <RHFTextField name='pickupPincode' label='Pickup (warehouse) pincode' inputProps={{maxLength: 6, inputMode: 'numeric'}} />
            {pickupHints.length > 0 && (
              <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: GAP.xs, mt: 1}}>
                <Typography sx={tertiaryTextSx}>Your pickup locations:</Typography>
                {pickupHints.slice(0, 8).map((p) => (
                  <Chip key={p} size='small' variant='outlined' label={p} onClick={() => setValue('pickupPincode', p, {shouldValidate: true})}
                    sx={{fontFamily: FONT, fontSize: SIZE['3xs'], height: 22}} />
                ))}
              </Box>
            )}
          </Grid>
          {error && <Grid item xs={12}><Box sx={alertStripSx(ERROR)}>{error}</Box></Grid>}
          <Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-end'}}>
            <LoadingButton type='submit' loading={busy} sx={primaryBtnSx}>Save rule</LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
    </MUIModal>
  );
}

const Count = ({label, value, color}) => (
  <Box sx={{flex: '1 1 100px', p: 1.25, borderRadius: '10px', bgcolor: `${color || SUBTLE}12`}}>
    <Typography sx={tertiaryTextSx}>{label}</Typography>
    <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.lg, color: color || NAVY}}>{(value || 0).toLocaleString('en-IN')}</Typography>
  </Box>
);

/** Check the file first (nothing saved), see what it will change, then confirm with the same file. */
function UploadModal({open, onClose, onDone, onTemplate, existing}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const {enqueueSnackbar} = useSnackbar();

  const reset = () => { setFile(null); setPreview(null); setError(''); };
  const close = () => { if (!busy) { reset(); onClose(); } };
  const pick = (e) => { setFile(e.target.files?.[0] || null); setPreview(null); setError(''); e.target.value = ''; };

  const run = async (confirm) => {
    setBusy(true);
    setError('');
    try {
      const res = await uploadMappings({file, confirm});
      if (!confirm) {
        setPreview(res);
      } else {
        enqueueSnackbar(`Saved ${res.validRows} rule${res.validRows === 1 ? '' : 's'}`, {variant: 'success'});
        if (res.invalidRows?.length) downloadErrors(res.invalidRows);
        reset();
        onDone();
      }
    } catch (e) {
      setError(errorText(e, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const invalid = preview?.invalidRows || [];
  const noChange = preview && !preview.added && !preview.changed;
  const action = !preview
    ? <LoadingButton disabled={!file} loading={busy} onClick={() => run(false)} sx={primaryBtnSx}>Check file</LoadingButton>
    : <LoadingButton loading={busy} disabled={noChange} onClick={() => run(true)} sx={primaryBtnSx}>Confirm upload</LoadingButton>;

  return (
    <MUIModal open={open} handleClose={close} title='Upload Pickup–Drop Mapping' maxWidth='sm' action={action}>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, color: NAVY, mb: 1}}>
        Two columns: <b>Drop (State or Pincode)</b> and <b>Pickup Pincode</b>.{' '}
        <Link component='button' onClick={onTemplate} sx={{fontFamily: FONT, fontSize: SIZE.sm, verticalAlign: 'baseline'}}>Download the template</Link>, fill the
        pickup pincode against the states you ship to, add pincode rows for exceptions, then upload it here. Blank pickups are skipped.
        {existing ? <> Rows in the file are <b>added or updated</b>; the {existing} existing rule{existing === 1 ? '' : 's'} not in the file stay as they are.</> : null}
      </Typography>
      <Box component='table' sx={{borderCollapse: 'collapse', mb: 2, '& td, & th': {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.5, pr: 2.5, textAlign: 'left', color: NAVY},
        '& th': {color: SUBTLE, fontWeight: WEIGHT.semibold, textTransform: 'uppercase', fontSize: SIZE['3xs']}}}>
        <thead><tr><th>Drop</th><th>Pickup Pincode</th><th>Meaning</th></tr></thead>
        <tbody>
          <tr><td>Haryana</td><td>110001</td><td>All of Haryana ships from 110001</td></tr>
          <tr><td>122001</td><td>122002</td><td>Exception: this pincode ships from 122002</td></tr>
          <tr><td>Kerala</td><td><i>(blank)</i></td><td>Skipped; uses the default pickup</td></tr>
        </tbody>
      </Box>
      <Button component='label' startIcon={<UploadFileRounded />} sx={secondaryBtnSx} disabled={busy}>
        {file ? file.name : 'Choose CSV file'}
        <input hidden type='file' accept='.csv' onChange={pick} />
      </Button>

      {preview && (
        <Box sx={{mt: 2}}>
          <Typography sx={{...labelSx, mb: 1}}>Against what is live now</Typography>
          <Box sx={{display: 'flex', flexWrap: 'wrap', gap: GAP.sm}}>
            <Count label='Added' value={preview.added} color={SUCCESS} />
            <Count label='Pickup changed' value={preview.changed} color={INFO} />
            <Count label='Unchanged' value={preview.unchanged} />
            <Count label='Blank (skipped)' value={preview.blankPickupRows} />
            <Count label='Errors' value={invalid.length} color={invalid.length ? ERROR : undefined} />
          </Box>
          {noChange && <Box sx={{...alertStripSx(INFO), mt: 1.5}}>This file matches what is live. Confirming changes nothing.</Box>}
          {invalid.length > 0 && (
            <Box sx={{...alertStripSx(ERROR), mt: 1.5}}>
              <span>{invalid.length} row{invalid.length > 1 ? 's' : ''} will be skipped. First: row {invalid[0].row}, {invalid[0].reason}</span>
              <Button onClick={() => downloadErrors(invalid)} sx={{...secondaryBtnSx, ml: 'auto'}}>Download</Button>
            </Box>
          )}
        </Box>
      )}
      {error && <Box sx={{...alertStripSx(ERROR), mt: 2}}>{error}</Box>}
    </MUIModal>
  );
}

export default withLayout(() => (
  <Box sx={pageContainerSx}>
    <ShopifyEddLocation />
  </Box>
));
